import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useWindowFocusRefresh } from "../hooks/useWindowFocusRefresh";
import ibkrStatusService, {
  isIbkrStatusDegraded,
  type IbkrConnectionStatus,
} from "../services/ibkrStatusService";
import { isCustomerViewBootProfile } from "../utils/adminCustomerViewBoot";

/**
 * PWA-2810: polls GET /v3/investor/ibkr-status for signed-in investors so the
 * shell can warn when IBKR data may be delayed or missing.
 *
 * - Enabled only for an authenticated actor holding INVESTOR (the endpoint 403s
 *   otherwise) with a resolved effective user, outside admin customer view.
 * - Fetches on enable and on every effective-user change, then every 5 minutes.
 *   Ticks are skipped while the tab is hidden; regaining focus refreshes once.
 * - A failed poll keeps the last known status; the next tick retries.
 */

export const IBKR_STATUS_POLL_INTERVAL_MS = 5 * 60_000;

export interface IbkrStatusContextValue {
  enabled: boolean;
  status: IbkrConnectionStatus | null;
  /** True for RESET_PERIOD and UNAVAILABLE. */
  isDegraded: boolean;
  refresh: () => Promise<void>;
}

const INERT_VALUE: IbkrStatusContextValue = {
  enabled: false,
  status: null,
  isDegraded: false,
  refresh: async () => undefined,
};

const IbkrStatusContext = createContext<IbkrStatusContextValue>(INERT_VALUE);

export const IbkrStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, effectiveUserId } = useAuth();
  const isInvestor = user?.roles?.includes("INVESTOR") ?? false;
  const enabled = isAuthenticated
    && !isLoading
    && isInvestor
    && Boolean(effectiveUserId)
    && !isCustomerViewBootProfile();

  const [status, setStatus] = useState<IbkrConnectionStatus | null>(null);
  const requestRef = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const effectiveUserIdRef = useRef(effectiveUserId);
  effectiveUserIdRef.current = effectiveUserId;

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabledRef.current) return;
    const reqId = ++requestRef.current;
    const userAtStart = effectiveUserIdRef.current;
    try {
      const response = await ibkrStatusService.getInvestorIbkrStatus();
      if (reqId !== requestRef.current || userAtStart !== effectiveUserIdRef.current) return;
      setStatus(response.status);
    } catch {
      // Keep the last known status; the next tick retries. Never surface the error.
    }
  }, []);

  // Reset on effective-user change; drop anything in flight for the previous user.
  useEffect(() => {
    requestRef.current += 1;
    setStatus(null);
  }, [effectiveUserId]);

  useEffect(() => {
    if (!enabled) {
      setStatus(null);
      return undefined;
    }
    void refresh();
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void refresh();
    };
    const timer = setInterval(tick, IBKR_STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, effectiveUserId, refresh]);

  useWindowFocusRefresh(() => {
    void refresh();
  });

  const value = useMemo<IbkrStatusContextValue>(
    () => (enabled
      ? { enabled: true, status, isDegraded: isIbkrStatusDegraded(status), refresh }
      : INERT_VALUE),
    [enabled, refresh, status]
  );

  return <IbkrStatusContext.Provider value={value}>{children}</IbkrStatusContext.Provider>;
};

export const useIbkrStatus = (): IbkrStatusContextValue => useContext(IbkrStatusContext);

export default IbkrStatusContext;
